
// Re-export all subscription-related functionality from the modular files
import type { Subscription } from '@/types/subscription';
import { Product, globalProducts, ProductStatus } from '@/types/product';
import { supabase } from '@/integrations/supabase/client';
import { getBillingPeriod } from '@/utils/billingPeriodUtils';

// Legacy global subscriptions array (for backward compatibility)
export let globalSubscriptions: Subscription[] = [];

// Map product to subscription for backward compatibility
const mapProductToSubscription = (product: Product): Subscription => {
  return {
    id: product.id,
    name: product.name || product.title,
    title: product.title,
    description: product.description || '',
    amount: product.amount || product.price_cents || 0,
    interval: product.billing_period === 'one_time' ? '' : product.billing_period,
    recurringPeriod: product.billing_period === 'one_time' ? '' : product.billing_period,
    currency: 'USD',
    created_at: product.created_at,
    status: product.status as ProductStatus,
    hero_image: product.hero_image,
    useAnchorBilling: product.use_anchor_billing,
    anchorBillingDay: product.anchor_billing_day,
    isSubscription: product.billing_period !== 'one_time'
  };
};

// Fetch subscriptions (now just returns filtered products)
export const fetchSubscriptions = async (): Promise<Subscription[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .neq('billing_period', 'one_time')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching subscriptions:', error);
      return [...globalSubscriptions];
    }

    if (data && data.length > 0) {
      // Transform database records to Product objects, then to Subscription objects
      const subscriptions = data.map(item => {
        const product: Product = {
          id: item.id,
          title: item.name, // Map from database field
          name: item.name,
          description: item.description || '',
          amount: item.price_cents, // Map from database field
          price_cents: item.price_cents,
          status: item.status as ProductStatus || 'active',
          created_at: item.created_at,
          billing_period: getBillingPeriod(item.billing_period),
          user_id: item.user_id
        };

        return mapProductToSubscription(product);
      });

      // Update global array
      globalSubscriptions = [...subscriptions];

      return [...globalSubscriptions];
    }

    return [...globalSubscriptions];
  } catch (error) {
    console.error('Error in fetchSubscriptions:', error);
    return [...globalSubscriptions];
  }
};

// Get subscription by ID 
export const getSubscriptionById = async (id: string): Promise<Subscription | null> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .neq('billing_period', 'one_time')
      .maybeSingle();

    if (error) {
      console.error(`Error fetching subscription by ID ${id}:`, error);
      return null;
    }

    if (data) {
      const product: Product = {
        id: data.id,
        title: data.name, // Map from database field
        name: data.name,
        description: data.description || '',
        amount: data.price_cents, // Map from database field
        price_cents: data.price_cents,
        status: (data.status as ProductStatus) || 'active', // Only 'active' or 'archived'
        created_at: data.created_at,
        billing_period: getBillingPeriod(data.billing_period),
        user_id: data.user_id
      };

      return mapProductToSubscription(product);
    }

    return null;
  } catch (error) {
    console.error(`Error in getSubscriptionById for ${id}:`, error);
    return null;
  }
};

// Helper function to fix subscriptions that may not have the is_subscription flag set
export const fixMissingSubscriptionFlags = async (): Promise<void> => {
  try {
    // Find products that look like subscriptions but have billing_period = one_time
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .neq('billing_period', 'one_time')
      .eq('is_test', false);

    if (error) {
      console.error('Error checking for unflagged subscriptions:', error);
      return;
    }

    if (data && data.length > 0) {
      console.log(`Found ${data.length} products that should be marked as subscriptions`);
      // No need for updates since we're using billing_period now
    }
  } catch (error) {
    console.error('Error in fixMissingSubscriptionFlags:', error);
  }
};

// Create subscription (simplified since we now just create a product)
export const createSubscription = (subscriptionData: any): Subscription => {
  // This is just a stub for backward compatibility
  return subscriptionData as Subscription;
};

// Add subscription (simplified to update global array)
export const addSubscription = async (subscription: Subscription): Promise<Subscription | null> => {
  // Store in global subscriptions array for backward compatibility
  globalSubscriptions.push(subscription);
  return subscription;
};

// Archive subscription
export const archiveSubscription = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('products')
      .update({ status: 'archived' })
      .eq('id', id);

    if (error) {
      console.error(`Error archiving subscription ${id}:`, error);
      return false;
    }

    return true;
  } catch (error) {
    console.error(`Error in archiveSubscription for ${id}:`, error);
    return false;
  }
};

// Use export type for the Subscription type
export type { Subscription };
